import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiTruck,
  FiMapPin,
  FiDollarSign,
  FiImage,
  FiShare2,
} from "react-icons/fi";
import { mealsAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/LoadingSpinner";
import toast from "react-hot-toast";

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 80px;
  padding-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  transition: color 0.2s;

  &:hover {
    color: #2d5016;
  }
`;

const MealCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const MealImage = styled.div`
  width: 100%;
  height: 400px;
  background: linear-gradient(135deg, #2d5016, #4a7c59);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 4rem;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const MealContent = styled.div`
  padding: 2rem;
`;

const MealHeader = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 0.5rem;
`;

const Description = styled.p`
  color: #666;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const VendorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const VendorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #2d5016;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.25rem;
`;

const VendorDetails = styled.div`
  flex: 1;
`;

const VendorName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 1rem;
`;

const VendorCuisine = styled.div`
  color: #666;
  font-size: 0.875rem;
`;

const MealStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #e0e0e0;
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: #2d5016;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d5016;
  margin-bottom: 1rem;
`;

const IngredientsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const IngredientChip = styled.span`
  padding: 0.5rem 1rem;
  background: #e6f2e6;
  border: 1px solid #2d5016;
  border-radius: 20px;
  font-size: 0.875rem;
  color: #2d5016;
`;

const AllergenChip = styled.span`
  padding: 0.5rem 1rem;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 20px;
  font-size: 0.875rem;
  color: #856404;
`;

const NutritionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const NutritionItem = styled.div`
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
`;

const NutritionValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 0.25rem;
`;

const NutritionLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled(Link)`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.$primary ? "#2d5016" : "white")};
  color: ${(props) => (props.$primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1rem;

  &:hover {
    background: ${(props) => (props.$primary ? "#1a3a0e" : "#f8f9fa")};
    transform: translateY(-2px);
  }
`;

const ShareButton = styled.button`
  max-width: 200px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: 2px solid #2d5016;
  background: white;
  color: #2d5016;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
    transform: translateY(-2px);
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;
`;

const MealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const {
    data: mealData,
    isLoading,
    error,
  } = useQuery(["meal", id], () => mealsAPI.getMeal(id), {
    enabled: !!id,
  });

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: mealData?.data?.data?.meal?.meal_name,
        text: mealData?.data?.data?.meal?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Container>
        <Content>
          <LoadingSpinner />
        </Content>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <h2>Error loading meal</h2>
            <p>{error.message || "Failed to load meal"}</p>
            <Button onClick={handleBack} $primary style={{ marginTop: "1rem" }}>
              <FiArrowLeft />
              Go Back
            </Button>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  const meal = mealData?.data?.data?.meal;

  if (!meal) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <h2>Meal not found</h2>
            <p>The meal you're looking for doesn't exist.</p>
            <Button onClick={handleBack} $primary style={{ marginTop: "1rem" }}>
              <FiArrowLeft />
              Go Back
            </Button>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  const basePrice = parseFloat(meal.base_price || 0);
  const deliveryFee = parseFloat(meal.delivery_fee || 0);
  const offersDelivery = meal.offers_delivery === true || meal.offers_delivery === "true" || meal.offers_delivery === 1;
  const offersPickup = meal.offers_pickup === true || meal.offers_pickup === "true" || meal.offers_pickup === 1;

  return (
    <Container>
      <Content>
        <BackButton onClick={handleBack}>
          <FiArrowLeft />
          Back to Meals
        </BackButton>

        <MealCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <MealImage>
            {meal.image_url ? (
              <img src={meal.image_url} alt={meal.meal_name} />
            ) : (
              <div className="placeholder">
                <FiImage size={64} />
              </div>
            )}
          </MealImage>

          <MealContent>
            <MealHeader>
              <Title>{meal.meal_name}</Title>
              {meal.description && <Description>{meal.description}</Description>}

              <VendorInfo>
                <VendorAvatar>
                  {meal.business_name?.charAt(0) || "V"}
                </VendorAvatar>
                <VendorDetails>
                  <VendorName>{meal.business_name}</VendorName>
                  {meal.cuisine_type && (
                    <VendorCuisine>{meal.cuisine_type} Cuisine</VendorCuisine>
                  )}
                </VendorDetails>
              </VendorInfo>
            </MealHeader>

            <MealStats>
              {offersDelivery && (
                <StatCard>
                  <StatIcon>
                    <FiTruck />
                  </StatIcon>
                  <StatContent>
                    <StatValue>
                      GYD ${Math.round(basePrice + deliveryFee).toLocaleString()}
                    </StatValue>
                    <StatLabel>Delivery</StatLabel>
                    {deliveryFee > 0 && (
                      <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.25rem" }}>
                        Base: ${Math.round(basePrice).toLocaleString()} + Delivery: ${Math.round(deliveryFee).toLocaleString()}
                      </div>
                    )}
                  </StatContent>
                </StatCard>
              )}

              {offersPickup && (
                <StatCard>
                  <StatIcon>
                    <FiMapPin />
                  </StatIcon>
                  <StatContent>
                    <StatValue>
                      GYD ${Math.round(basePrice).toLocaleString()}
                    </StatValue>
                    <StatLabel>Pickup</StatLabel>
                    <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.25rem" }}>
                      {meal.pickup_address || "Vendor location"}
                    </div>
                  </StatContent>
                </StatCard>
              )}

              {!offersDelivery && !offersPickup && (
                <StatCard>
                  <StatIcon>
                    <FiDollarSign />
                  </StatIcon>
                  <StatContent>
                    <StatValue>
                      GYD ${Math.round(basePrice).toLocaleString()}
                    </StatValue>
                    <StatLabel>Price</StatLabel>
                  </StatContent>
                </StatCard>
              )}
            </MealStats>

            {meal.ingredients && meal.ingredients.length > 0 && (
              <Section>
                <SectionTitle>Ingredients</SectionTitle>
                <IngredientsList>
                  {meal.ingredients.map((ingredient, index) => (
                    <IngredientChip key={index}>
                      {ingredient.name || ingredient}
                    </IngredientChip>
                  ))}
                </IngredientsList>
              </Section>
            )}

            {meal.allergens && meal.allergens.length > 0 && (
              <Section>
                <SectionTitle>Allergens</SectionTitle>
                <IngredientsList>
                  {meal.allergens.map((allergen, index) => (
                    <AllergenChip key={index}>{allergen}</AllergenChip>
                  ))}
                </IngredientsList>
              </Section>
            )}

            {(meal.calories || meal.protein || meal.carbs || meal.fat) && (
              <Section>
                <SectionTitle>Nutrition Information</SectionTitle>
                <NutritionGrid>
                  {meal.calories && (
                    <NutritionItem>
                      <NutritionValue>{meal.calories}</NutritionValue>
                      <NutritionLabel>Calories</NutritionLabel>
                    </NutritionItem>
                  )}
                  {meal.protein && (
                    <NutritionItem>
                      <NutritionValue>{meal.protein}g</NutritionValue>
                      <NutritionLabel>Protein</NutritionLabel>
                    </NutritionItem>
                  )}
                  {meal.carbs && (
                    <NutritionItem>
                      <NutritionValue>{meal.carbs}g</NutritionValue>
                      <NutritionLabel>Carbs</NutritionLabel>
                    </NutritionItem>
                  )}
                  {meal.fat && (
                    <NutritionItem>
                      <NutritionValue>{meal.fat}g</NutritionValue>
                      <NutritionLabel>Fat</NutritionLabel>
                    </NutritionItem>
                  )}
                </NutritionGrid>
              </Section>
            )}

            {offersPickup && meal.pickup_instructions && (
              <Section>
                <SectionTitle>Pickup Instructions</SectionTitle>
                <p style={{ color: "#666", lineHeight: 1.6 }}>
                  {meal.pickup_instructions}
                </p>
              </Section>
            )}

            <ActionButtons>
              <ShareButton onClick={handleShare}>
                <FiShare2 />
                Share
              </ShareButton>
              <Button
                $primary
                to={isAuthenticated ? `/meals/${meal.id}/order` : "/login"}
              >
                {isAuthenticated ? "Order Now" : "Login to Order"}
              </Button>
            </ActionButtons>
          </MealContent>
        </MealCard>
      </Content>
    </Container>
  );
};

export default MealDetail;

